// src/components/froggerAI/listeningSession.js
//
// CONVERSATION MODE — the state machine (I-03 R6 · S3-06.3, §3f).
//
// Operator ruling: "hands are free-ish… sometimes wet, with gloves." So
// one tap opens a LISTENING SESSION that stays open across turns: the
// mic is armed, a voice-activity detector segments what is said, each
// segment becomes a turn, the reply renders, and the mic re-arms. The
// keeper never touches the screen again until they want to stop.
//
// ── WHY THIS FILE HAS NO AUDIO IN IT ──────────────────────────────────
//
// Everything here is a pure transition over an explicit event. Nothing
// in this module touches `navigator`, `MediaRecorder`, a worklet or a
// timer it was not handed. That is what makes the session TESTABLE
// without a microphone — and the session is the part that can be wrong
// in ways a human cannot see: a mic that says LISTENING while the tab is
// backgrounded and the track is suspended is the failure the proposal
// names explicitly ("never pretends to be listening").
//
// `VoiceRecorder.jsx` owns the audio and feeds this events.
//
// ── THE STATES, and what each one PROMISES the operator ───────────────
//
//   idle       nothing is open. Tap to start.
//   starting   the gesture has been made; permission / model loading.
//   listening  the mic is OPEN and nobody is talking. Silence counts.
//   hearing    speech is being captured right now.
//   thinking   a segment is with Whisper and the graph.
//   paused     the page was backgrounded (or the OS took the mic). The
//              track is stopped. "Tap to resume" — never "listening".
//   ended      over, with a REASON the UI says out loud.
//
// ── TWO MINUTES OF QUIET ENDS IT ──────────────────────────────────────
//
// Not a nicety: an open mic that outlives the round is a mic recording a
// room nobody is in. The timer runs only in `listening`; speech clears
// it, and it re-arms when the mic re-arms.

export const IDLE = 'idle';
export const STARTING = 'starting';
export const LISTENING = 'listening';
export const HEARING = 'hearing';
export const THINKING = 'thinking';
export const PAUSED = 'paused';
export const ENDED = 'ended';

/** 2 minutes of silence closes the session (proposal §3f). */
export const SILENCE_TIMEOUT_MS = 120000;

/**
 * What the operator reads for each state. Short: it is read at a glance.
 *
 * ⚠️ THESE ARE THE ONLY STATUS WORDS ON THE DOCK DURING A VOICE SESSION
 * (operator ruling, phone pass on beta 2026-09-13). The collapsed dock
 * used to carry three at once — "Working" in the composer, "Thinking"
 * here, "Thinking…" on a spinner row of its own — for one wait, on the
 * narrowest screen in the app. `VoiceRecorder` reports whichever of
 * these it is showing up to the panel (`onStatusChange`) and the panel's
 * own row stands down while it does. `THINKING` carries the ellipsis
 * because it is now the token the OTHER surfaces used to spell.
 */
export const STATE_LABELS = {
  [IDLE]: 'Tap to talk',
  [STARTING]: 'Opening the mic…',
  [LISTENING]: 'Listening',
  [HEARING]: 'Hearing you',
  [THINKING]: 'Thinking…',
  [PAUSED]: 'Paused — tap to resume',
  [ENDED]: 'Session closed',
};

/**
 * THE RECORDING CONTAINER, chosen by asking the browser (§3f).
 *
 * iOS Safari's MediaRecorder emits `audio/mp4` (AAC) and does NOT
 * support webm; the old recorder hard-coded webm with mp4 as a fallback,
 * which is backwards for the device this sprint is designed for. Whisper
 * accepts m4a, so the order is simply "whatever this browser can
 * actually produce", most-preferred first.
 *
 * Returns `''` when nothing is supported, which callers pass to
 * MediaRecorder as "your default" rather than a container it will refuse.
 */
export const MIME_PREFERENCE = [
  'audio/mp4',          // iOS Safari — the iPad this is built for
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

export const pickMimeType = (isTypeSupported) => {
  if (typeof isTypeSupported !== 'function') return '';
  for (let i = 0; i < MIME_PREFERENCE.length; i += 1) {
    try {
      if (isTypeSupported(MIME_PREFERENCE[i])) return MIME_PREFERENCE[i];
    } catch (err) { /* a throwing probe is a "no" */ }
  }
  return '';
};

/** States in which the mic is actually open and the operator is told so. */
export const LIVE_STATES = [LISTENING, HEARING, THINKING];

/**
 * Create a session.
 *
 * Dependencies are INJECTED (`timers`, `now`) so a test can drive two
 * minutes of silence in a millisecond and so the machine has no global
 * reach of its own.
 *
 *   onState(state, detail)   — every transition, including no-ops? NO:
 *                              only real changes, so a UI can re-render
 *                              on it without churning.
 *   onEnd(reason)            — once, when the session closes.
 * ⚠️ `timers` DEFAULTS TO WRAPPERS, NOT TO THE BARE GLOBALS. It was
 * `{ setTimeout, clearTimeout }`, which puts the plain object in the
 * receiver position — and a browser's `setTimeout` lives on Window with
 * a WebIDL receiver check, so `timers.setTimeout(…)` threw
 * "Illegal invocation" (Chromium) / "Can only call Window.setTimeout on
 * instances of Window" (WebKit) on the FIRST arm. That arm is inside
 * `case 'ready'`, so conversation mode died the instant the microphone
 * opened, in EVERY real browser, showing the operator "Illegal
 * invocation" where it should have said Listening. Every unit test
 * passed the whole time: jsdom does not enforce the receiver.
 * Found by the WebKit/iPad harness, S3-06.3c. Call them BARE.
 *
 *   silenceMs                — a number, or a FUNCTION returning one,
 *                              read each time the clock is armed. The
 *                              function form is the test seam S3-06.3c
 *                              needs: the simulated-voice harness cannot
 *                              wait two real minutes to prove the
 *                              session closes itself, and the value it
 *                              wants is not known when the component
 *                              first renders. A plain number behaves
 *                              exactly as it always did.
 */
export function createListeningSession({
  onState,
  onEnd,
  silenceMs = SILENCE_TIMEOUT_MS,
  timers = {
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (id) => clearTimeout(id),
  },
} = {}) {
  let state = IDLE;
  let silenceTimer = null;
  let endReason = null;

  const clearSilence = () => {
    if (silenceTimer !== null) {
      timers.clearTimeout(silenceTimer);
      silenceTimer = null;
    }
  };

  /** Read at ARM time, not at construction — see `silenceMs` above. */
  const resolveSilenceMs = () => {
    const value = typeof silenceMs === 'function' ? silenceMs() : silenceMs;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : SILENCE_TIMEOUT_MS;
  };

  const armSilence = () => {
    clearSilence();
    silenceTimer = timers.setTimeout(() => {
      silenceTimer = null;
      // eslint-disable-next-line no-use-before-define
      end('silence');
    }, resolveSilenceMs());
  };

  const to = (next, detail) => {
    if (next === state) return state;
    state = next;
    onState?.(state, detail);
    return state;
  };

  function end(reason) {
    if (state === ENDED) return state;
    clearSilence();
    endReason = reason || 'stopped';
    to(ENDED, { reason: endReason });
    onEnd?.(endReason);
    return state;
  }

  /**
   * ONE ENTRY POINT. Every transition is named, and an event that makes
   * no sense in the current state is IGNORED rather than forced — a VAD
   * that fires `speech_end` while the page is paused must not resurrect
   * a mic that has been stopped.
   */
  const dispatch = (event, payload) => {
    switch (event) {
      // The user gesture. iOS Safari needs one per page load, and this
      // is it — everything after this is the session staying open.
      case 'start':
        if (state === STARTING || LIVE_STATES.includes(state)) return state;
        endReason = null;
        return to(STARTING);

      // getUserMedia resolved and the detector is loaded.
      case 'ready':
        if (state !== STARTING) return state;
        armSilence();
        return to(LISTENING);

      case 'start_failed':
        clearSilence();
        endReason = payload?.reason || 'error';
        to(ENDED, { reason: endReason, error: payload?.error });
        onEnd?.(endReason);
        return state;

      case 'speech_start':
        if (state !== LISTENING) return state;
        clearSilence();
        return to(HEARING);

      // A segment was captured. It goes to Whisper; the mic is still
      // open, but the operator is told we are working on what they said.
      // The payload carries the DETECTOR's own samples (a Float32Array
      // at 16 kHz, padding included) — `wavEncoder` turns it into the
      // upload. Nothing here touches audio.
      case 'speech_end':
        if (state !== HEARING) return state;
        return to(THINKING, { samples: payload?.samples });

      // The detector decided that was not speech after all (a mister, a
      // door). Back to listening WITHOUT a turn — and the silence clock
      // restarts, because nothing was said.
      case 'speech_cancel':
        if (state !== HEARING) return state;
        armSilence();
        return to(LISTENING);

      // The turn finished, well or badly. Either way the mic re-arms:
      // a failed transcription must not silently close the session the
      // keeper is relying on.
      case 'turn_done':
        if (state !== THINKING) return state;
        armSilence();
        return to(LISTENING);

      // `visibilitychange`. The track is suspended by the OS on a screen
      // lock or a backgrounded tab, so the honest state is PAUSED and
      // resuming is the operator's deliberate act.
      case 'hidden':
        if (state === IDLE || state === ENDED || state === PAUSED) return state;
        clearSilence();
        return to(PAUSED);

      case 'resume':
        if (state !== PAUSED) return state;
        return to(STARTING);

      case 'stop':
        return end(payload?.reason || 'stopped');

      default:
        return state;
    }
  };

  return {
    dispatch,
    get state() { return state; },
    get endReason() { return endReason; },
    /** For teardown: never leaves a timer behind on unmount. */
    destroy: () => { clearSilence(); },
    /** Test seam — whether the silence clock is currently running. */
    get silenceArmed() { return silenceTimer !== null; },
  };
}

export default createListeningSession;
